import { useId } from 'react'
import { MASCOT_COLORS, type MascotConfig } from '../mascot-catalog'
import { mascotFurPalette } from '../mascot-colors'
import { AgroplayEmblem, SheepHat } from '../SheepMascotParts'
import { MascotHeadwear } from '../MascotDecorations'
import { MascotAccessory } from '../MascotDetails'
import type { frisbeePetPose, LegPose } from './frisbee-pet-motion'
import { FrisbeeDisc } from './FrisbeeDisc'

function Hoof({
  pose,
  x,
  y,
  far = false,
}: {
  pose: LegPose
  x: number
  y: number
  far?: boolean
}) {
  const [kx, ky] = pose.knee
  const [px, py] = pose.paw
  return (
    <g
      opacity={far ? 0.78 : 1}
      strokeLinecap="round"
      strokeLinejoin="round"
      data-sheep-hoof
    >
      <path
        d={`M${x} ${y}Q${x} ${ky} ${kx} ${ky}L${px} ${py - 3}`}
        fill="none"
        stroke={far ? '#cc9585' : '#f1c0ae'}
        strokeWidth="6"
      />
      <path
        d={`M${px - 3} ${py - 5}q4 2 8 0l2 5q-6 5-11 0Z`}
        fill="#a16859"
        stroke="#815046"
        strokeWidth=".6"
      />
      <path d={`M${px + 2} ${py - 1}v3`} stroke="#794b42" strokeWidth=".8" />
    </g>
  )
}

/** All motion comes from the same sampled game clock as the other species. */
export function SheepRunner({
  config,
  pose,
  at,
  carryingFrisbee,
}: {
  config: MascotConfig
  pose: ReturnType<typeof frisbeePetPose>
  at: number
  carryingFrisbee: boolean
}) {
  const id = `sheep-run-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const p = mascotFurPalette(
    config.furColor ??
      (config.color === 'cream' ? '#fff5e5' : MASCOT_COLORS[config.color])
  )
  return (
    <g
      data-runner-pet="sheep"
      data-gait={pose.airborne ? 'jump' : at === 0 ? 'idle' : 'gallop'}
    >
      <defs>
        <linearGradient id={id} x2="0" y2="1">
          <stop stopColor={p.light} />
          <stop offset="1" stopColor={p.mid} />
        </linearGradient>
      </defs>
      <Hoof pose={pose.farHind} x={-28} y={-28 + pose.bodyY} far />
      <Hoof pose={pose.farFront} x={10} y={-28 + pose.bodyY} far />
      <g transform={`translate(0 ${pose.bodyY}) rotate(${pose.angle} -5 -33)`}>
        <g transform={`rotate(${pose.tail} -32 -36)`}>
          <path d="M-31-38q-14-14-17-5-6 8 7 13Z" fill={p.light} stroke="#e2ccb4" />
        </g>
        <g
          transform={`translate(-6 -32) scale(${pose.stretch} ${1 / pose.stretch}) translate(6 32)`}
          data-runner-fleece
        >
          <path
            d="M-35-38q-6-9 4-13 2-9 13-6 8-7 16-2 10-6 18 1 12-3 15 8 9 1 7 11 7 7-1 14 1 9-10 10-7 8-16 3-10 6-17-1-12 5-16-5-11 0-10-10Z"
            fill={`url(#${id})`}
            stroke="#dfcbb3"
            strokeWidth=".9"
          />
          <path
            d="M-24-45q-3-6 4-7m9 18q-7 0-7 6m26-20q5-3 9 2M-4-23q7 5 12-1"
            fill="none"
            stroke="#e4d1b7"
            strokeWidth="1"
            strokeLinecap="round"
          />
        </g>
      </g>
      <Hoof pose={pose.hind} x={-25} y={-27 + pose.bodyY} />
      <Hoof pose={pose.front} x={16} y={-27 + pose.bodyY} />
      <g
        transform={`translate(0 ${pose.bodyY * 0.7}) rotate(${pose.headAngle + pose.angle * 0.35} 18 -36)`}
      >
        {config.accessory === 'agroplay_bandana' ? (
          <g>
            <path
              d="M8-43q13 7 27-1L27-24 13-30Z"
              fill={config.accessoryColor ?? '#20a64a'}
              stroke="#237b43"
              strokeWidth=".7"
            />
            <g transform="translate(23 -33) scale(.22)" color="#fff9e7">
              <AgroplayEmblem />
            </g>
          </g>
        ) : (
          <g transform="translate(20 -39) scale(.27) translate(-120 -166)">
            <MascotAccessory
              accessory={config.accessory}
              accessoryColor={config.accessoryColor}
            />
          </g>
        )}
        <path
          d="M6-55Q3-74 20-78Q40-81 44-63L47-54Q58-51 55-42Q49-33 35-37L18-39Q6-42 6-55Z"
          fill="#f3c4b7"
          stroke="#d99b8d"
          strokeWidth=".9"
        />
        {config.headwear === 'ranch_hat' && (
          <g transform="translate(24 -70) scale(.33) translate(-120 -69)">
            <SheepHat color={config.headwearColor} />
          </g>
        )}
        <path
          d="M7-56Q-2-61 3-68q-2-9 7-10 3-9 12-5 9-6 16 2 10-1 10 8 5 7-2 10-8 1-10-7-8 6-14 0-8 7-15 2Z"
          fill={p.light}
          stroke="#e4d2ba"
          strokeWidth=".7"
        />
        <g data-runner-sheep-ear transform={`rotate(${pose.ear} 10 -61)`}>
          <path
            d="M11-65Q-7-63-11-44Q-2-38 15-55Z"
            fill={p.light}
            stroke="#e4d2ba"
            strokeWidth=".7"
          />
          <path d="M8-61Q-3-57-7-46Q3-46 11-55Z" fill="#e9aab9" />
        </g>
        <ellipse cx="34" cy="-57" rx="7" ry="10" fill="#fffdf5" />
        <ellipse cx="36" cy="-57" rx="5" ry="8" fill={config.eyeColor ?? '#81b64d'} />
        <ellipse cx="36" cy="-57" rx="2.5" ry="4.7" fill="#684332" />
        <ellipse cx="34" cy="-61" rx="2" ry="2.8" fill="#fff" />
        <circle cx="39" cy="-53" r="1.3" fill="#fff" />
        <path
          d="M28-63q6-8 12 0m0-1 3-3"
          fill="none"
          stroke="#7a4946"
          strokeWidth="1.1"
          strokeLinecap="round"
        />
        <path d="M28-70q4-2 8 0" stroke="#92594f" strokeWidth="2" strokeLinecap="round" />
        <g data-runner-sheep-glasses fill="none" stroke="#ce8c81" strokeWidth=".9">
          <ellipse cx="34" cy="-56" rx="10" ry="12" />
          <path d="m24-58-10-3m30 7 5 1" />
        </g>
        <path d="m48-47 6 1-4 4Z" fill="#d58e9e" />
        <path
          d="m50-42-2 4q-4 3-7-1"
          stroke="#754740"
          strokeWidth="1"
          strokeLinecap="round"
          fill="none"
        />
        <ellipse cx="29" cy="-42" rx="5" ry="2.5" fill="#e8979b" opacity=".35" />
        {config.headwear !== 'ranch_hat' && (
          <g transform="translate(24 -72) scale(.25) translate(-120 -50)">
            <MascotHeadwear config={config} />
          </g>
        )}
        {carryingFrisbee && (
          <g transform="translate(64 -35) rotate(-9)" data-carrying-frisbee>
            <FrisbeeDisc />
            <path d="M-22-2q4 5 8 0" stroke="#92594f" strokeWidth="1.6" fill="none" />
          </g>
        )}
      </g>
    </g>
  )
}
